import {
	useApiGetBreadcrumbTeam,
	useApiGetEntityTeams,
} from '@repo/api-codegen';
import { stripMarkdownLinks } from '@repo/common/utils';
import { type BreadcrumbCrumb } from '@repo/foundations';
import { reverse } from 'lodash-es';
import type {
	IApiListResponse,
	INestedBreadcrumbs,
	ISecodaEntity,
} from '../../api';
import { useIntegrationList } from '../../api';
import { useNestedBreadcrumbsList } from '../../api/hooks/nestedBreadcrumbs';
import { getBreadcrumbForEntity } from '../../utils/breadcrumb';
import { useEntityBreadcrumbTeam } from './useEntityBreadcrumbTeam';

interface IUseEntityBreadcrumbsReturn {
	isLoading: boolean;
	breadcrumbs: Array<BreadcrumbCrumb>;
}

interface IUseEntityBreadcrumbsOptions {
	includeTeam?: boolean;
	includeCurrentPage?: boolean;
}

/**
 * Generates breadcrumbs for the given SecodaEntity.
 */
export function useEntityBreadcrumbs(
	entity: ISecodaEntity,
	options?: IUseEntityBreadcrumbsOptions
): IUseEntityBreadcrumbsReturn {
	const { teamBreadcrumb: preferredTeamId } = useEntityBreadcrumbTeam();

	const { data: team, isLoading: isTeamLoading } = useApiGetBreadcrumbTeam({
		pathParams: {
			entityId: entity.id,
		},
		queryParams: {
			preferred_team_id: preferredTeamId,
		},
	});

	const { data: inheritedTeams, isLoading: isInheritedTeamsLoading } =
		useApiGetEntityTeams({
			queryParams: { entity_id: entity.id },
		});

	const { data: integrations, isLoading: isIntegrationsListLoading } =
		useIntegrationList({
			options: {
				select: (res) => res.results,
			},
		});

	const { data: nestedBreadcrumbs, isLoading: isNestedBreadCrumbsLoading } =
		useNestedBreadcrumbsList({
			filters: {
				id: entity.id,
			},
			options: {
				select: (res: IApiListResponse<INestedBreadcrumbs>) =>
					reverse(res.results || []),
			},
		});

	const waitForFallback = !team && isInheritedTeamsLoading;
	if (
		isNestedBreadCrumbsLoading ||
		isIntegrationsListLoading ||
		isTeamLoading ||
		waitForFallback
	) {
		return { isLoading: true, breadcrumbs: [] };
	}

	// When breadcrumb team API returns null, we need a fallback so links aren't null
	// All four sources are needed, each covers cases the others don't:
	//
	// 1. assigned_teams — v2 source of truth from inherited_teams API. Needed because
	//    entity.teams is often omitted on list responses and integration_inheritance only
	//    applies when the entity has an integration, many entities have only direct assignment.
	//
	// 2. integration_inheritance — team from the entity's integration. Needed because some
	//    entities have team access only via their integration (no direct assigned_teams),
	//    assigned_teams can be empty for those.
	//
	// 3. entity.teams — teams on the entity from list/detail. Needed because inherited_teams
	//    can be slow, fail or not yet loaded, use teams without waiting.
	//
	// 4. null — no fallback
	const fallbackTeamId = team
		? null
		: (inheritedTeams?.assigned_teams?.[0] ??
			inheritedTeams?.integration_inheritance?.[0] ??
			entity.teams?.[0] ??
			null);

	return {
		isLoading: false,
		breadcrumbs: getBreadcrumbForEntity({
			entity,
			nativeType: entity.native_type ?? '',
			team: team || null,
			fallbackTeamId,
			nestedBreadcrumbs,
			integrations,
			includeTeam: options && options.includeTeam,
			includeCurrentPage: options && options.includeCurrentPage,
		}).map((crumb) => ({
			...crumb,
			title: stripMarkdownLinks(crumb.title),
		})),
	};
}
